import { useState } from 'react';
import { usePermissions, Permission } from '../../../shared/hooks/usePermissions';
import { useClaimPermission } from '../../../shared/hooks/useClaimPermission';
import Button from '../../../shared/components/Button';
import {
  useContractTemplates,
  useCreateContractTemplate,
  useCreateContractTemplateVersion,
  useActivateContractTemplate,
} from '../hooks/useContractTemplates';
import {
  ContractTemplateFormModal,
  type ContractTemplateSubmitValues,
} from '../components/ContractTemplateFormModal';
import { ContractTemplateFieldsModal } from '../components/ContractTemplateFieldsModal';
import { CONTRACT_TYPE_LABELS, type ContractTemplate } from '../types';

export function ContractTemplatesPage() {
  const { hasPermission } = usePermissions();
  const { hasClaim } = useClaimPermission();
  // PE-2147 — a department role (e.g. Legal) can grant MERCHANT_ONBOARDING:MANAGE,
  // which is the resource osprey-api's contract-templates controller enforces.
  // That grant only exists in the JWT claim, so OR it with the static role gate.
  const canManage =
    hasPermission(Permission.CONTRACT_TEMPLATE_MANAGE) || hasClaim('MERCHANT_ONBOARDING', 'MANAGE');

  const { data: templates = [], isLoading, error } = useContractTemplates();
  const createTemplate = useCreateContractTemplate();
  const createVersion = useCreateContractTemplateVersion();
  const activate = useActivateContractTemplate();

  // `null` baseTemplate = create; a template = new-version. Undefined = modal closed.
  const [formBase, setFormBase] = useState<ContractTemplate | null | undefined>(undefined);
  const [fieldsFor, setFieldsFor] = useState<ContractTemplate | null>(null);

  const isSaving = createTemplate.isPending || createVersion.isPending;

  const handleFormSubmit = async (values: ContractTemplateSubmitValues) => {
    if (formBase) {
      await createVersion.mutateAsync({
        name: formBase.name,
        data: { contentHtml: values.contentHtml, documentUrl: values.documentUrl, contractType: values.contractType },
      });
    } else {
      await createTemplate.mutateAsync(values);
    }
    setFormBase(undefined);
  };

  return (
    <div className="py-6 max-w-6xl mx-auto px-2">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-[23px] font-bold text-foreground">Contract Templates</h1>
          <p className="text-[14px] text-muted-foreground mt-1">
            Upload and version the merchant onboarding contracts, and set which details are required to sign.
          </p>
        </div>
        {canManage && (
          <Button size="sm" className="h-[40px]" onClick={() => setFormBase(null)}>
            New template
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="text-center py-16 text-muted-foreground">Loading contract templates…</div>
      ) : error ? (
        <div className="text-center py-16 text-red-500">Failed to load contract templates. Please try again.</div>
      ) : templates.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground text-[14px] border border-dashed border-[#D9D9D9] rounded-[16px]">
          No contract templates yet.
          {canManage && ' Click "New template" to create one.'}
        </div>
      ) : (
        <div className="bg-card rounded-2xl border border-border overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted text-left">
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Name</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Version</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Type</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Document</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Status</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {templates.map((t) => (
                <tr key={t.id} className="hover:bg-muted transition-colors align-middle">
                  <td className="px-6 py-4 text-sm font-medium text-foreground">{t.name}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">v{t.version}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">
                    {t.contractType ? CONTRACT_TYPE_LABELS[t.contractType] : '—'}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    {t.documentUrl ? (
                      <a href={t.documentUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                        View
                      </a>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`text-[11px] font-[600] px-2 py-0.5 rounded-full ${
                        t.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {t.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-4">
                      <button onClick={() => setFieldsFor(t)} className="text-xs text-blue-600 hover:underline">
                        Configure fields
                      </button>
                      {canManage && (
                        <>
                          <button onClick={() => setFormBase(t)} className="text-xs text-blue-600 hover:underline">
                            New version
                          </button>
                          {!t.isActive && (
                            <button
                              onClick={() => activate.mutate(t.id)}
                              disabled={activate.isPending}
                              className="text-xs text-emerald-700 hover:underline disabled:opacity-50"
                            >
                              Activate
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {formBase !== undefined && (
        <ContractTemplateFormModal
          isOpen
          baseTemplate={formBase}
          onClose={() => setFormBase(undefined)}
          onSubmit={handleFormSubmit}
          isSaving={isSaving}
        />
      )}

      <ContractTemplateFieldsModal
        isOpen={!!fieldsFor}
        template={fieldsFor}
        onClose={() => setFieldsFor(null)}
      />
    </div>
  );
}
